// react-query hooks over the resource client (ADR 0005, ADR 0007).
// Choose by need: a paged table → useList (the UI MUST show pagination);
// anything "all of X" → useAllRecords; infinite scroll → useInfiniteList.
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { keys } from './keys'
import { getAllPages, type AllPagesResult } from './pagination'
import { resource } from './resource'
import type { ListQuery, ListResult, RecordId, SortOrder } from './types'

interface QueryToggle {
  enabled?: boolean
}

/** A ListQuery, or anything carrying one (the return value of useListParams). */
export type ListParamsInput = ListQuery | { query: ListQuery }

function toQuery(params: ListParamsInput): ListQuery {
  return 'query' in params ? params.query : params
}

/** One page of a list. Keeps the previous page on screen while the next one loads. */
export function useList<T>(res: string, params: ListParamsInput, options: QueryToggle = {}) {
  const query = toQuery(params)
  return useQuery<ListResult<T>>({
    queryKey: keys.list(res, query),
    queryFn: ({ signal }) => resource<T>(res).list(query, { signal }),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true
  })
}

export interface AllRecordsParams extends QueryToggle {
  sort?: string
  order?: SortOrder
  filter?: NonNullable<ListQuery['filter']>
  params?: NonNullable<ListQuery['params']>
}

/** Every record of a resource (pickers, filter options, transfer lists, export). */
export function useAllRecords<T>(res: string, { enabled, ...params }: AllRecordsParams = {}) {
  return useQuery<AllPagesResult<T>>({
    queryKey: keys.allRecords(res, params),
    queryFn: ({ signal }) => getAllPages<T>(res, { ...params, signal }),
    enabled: enabled ?? true
  })
}

export interface InfiniteListParams extends AllRecordsParams {
  /** The size of the first request; later pages use the observed size. The portal uses 50. */
  perPage?: number
}

interface InfinitePageParam {
  offset: number
  size: number
}

/**
 * Infinite scroll (upgrade-process tenants). The page size is the observed
 * length of the first response, like getAllPages; a short page ends the list.
 */
export function useInfiniteList<T>(
  res: string,
  { enabled, perPage = 50, ...params }: InfiniteListParams = {}
) {
  const base = params
  const query = useInfiniteQuery({
    queryKey: keys.infinite(res, { ...base, perPage }),
    initialPageParam: { offset: 0, size: perPage },
    queryFn: ({ pageParam, signal }) =>
      resource<T>(res).list(
        { ...base, start: pageParam.offset, end: pageParam.offset + pageParam.size },
        { signal }
      ),
    getNextPageParam: (lastPage, allPages, lastParam): InfinitePageParam | undefined => {
      const pageSize = allPages[0]?.rows.length ?? 0
      if (pageSize === 0 || lastPage.rows.length === 0) return undefined
      if (lastPage.hasNextPage === false) return undefined
      // A known total ends the list exactly, as react-admin's InfiniteList does
      // (no confirming empty request on an exact multiple of the page size).
      const loaded = allPages.reduce((n, page) => n + page.rows.length, 0)
      if (lastPage.total !== undefined && loaded >= lastPage.total) return undefined
      if (lastPage.hasNextPage === undefined && lastPage.rows.length < pageSize) return undefined
      return { offset: lastParam.offset + lastPage.rows.length, size: pageSize }
    },
    enabled: enabled ?? true
  })
  const pages = query.data?.pages
  const rows = useMemo(() => {
    const seen = new Set<RecordId>()
    const out: T[] = []
    for (const row of (pages ?? []).flatMap((page) => page.rows)) {
      const id = (row as { id?: RecordId | null } | null)?.id
      if (id !== undefined && id !== null) {
        if (seen.has(id)) continue
        seen.add(id)
      }
      out.push(row)
    }
    return out
  }, [pages])
  return { ...query, rows, total: pages?.[0]?.total }
}

export interface RecordOptions extends QueryToggle {
  /** Sent as X-TenantId (profiles only — PARITY Q1). */
  tenantId?: string | number
}

/** One record. Disabled while `id` is null/undefined/''. */
export function useRecord<T>(
  res: string,
  id: RecordId | null | undefined,
  { tenantId, enabled }: RecordOptions = {}
) {
  const ready = id !== null && id !== undefined && id !== ''
  return useQuery<T>({
    queryKey: ready ? keys.one(res, id, tenantId) : [res, 'one', null],
    queryFn: ({ signal }) =>
      resource<T>(res).one(id as RecordId, { signal, ...(tenantId !== undefined && { tenantId }) }),
    enabled: ready && (enabled ?? true)
  })
}

export interface MutationOptions {
  /** Other resources to refresh on success (e.g. a wave update also refreshes `tenants`). */
  related?: readonly string[]
  /** The caller reports errors itself; the global onMutationError hook stays quiet. */
  silent?: boolean
}

async function invalidate(client: QueryClient, res: string, related: readonly string[] = []) {
  await Promise.all(
    [res, ...related].map((name) => client.invalidateQueries({ queryKey: keys.all(name) }))
  )
}

function meta(options: MutationOptions) {
  return options.silent ? { meta: { silent: true } } : {}
}

export function useCreate<T>(res: string, options: MutationOptions = {}) {
  const client = useQueryClient()
  return useMutation<T, unknown, Partial<T> | Record<string, unknown>>({
    mutationFn: (data) => resource<T>(res).create(data),
    onSuccess: () => invalidate(client, res, options.related),
    ...meta(options)
  })
}

export interface UpdateVariables<T> {
  id: RecordId
  data: Partial<T> | Record<string, unknown>
  tenantId?: string | number
}

export function useUpdate<T>(res: string, options: MutationOptions = {}) {
  const client = useQueryClient()
  return useMutation<T, unknown, UpdateVariables<T>>({
    mutationFn: ({ id, data, tenantId }) =>
      resource<T>(res).update(id, data, tenantId !== undefined ? { tenantId } : {}),
    onSuccess: () => invalidate(client, res, options.related),
    ...meta(options)
  })
}

export interface DeleteVariables {
  id: RecordId
  tenantId?: string | number
}

export function useDelete(res: string, options: MutationOptions = {}) {
  const client = useQueryClient()
  return useMutation<unknown, unknown, DeleteVariables>({
    mutationFn: ({ id, tenantId }) =>
      resource(res).remove(id, tenantId !== undefined ? { tenantId } : {}),
    onSuccess: async (_data, { id }) => {
      client.removeQueries({ queryKey: [res, 'one', id] })
      await invalidate(client, res, options.related)
    },
    ...meta(options)
  })
}
